<template>
  <div>
    <v-dialog v-model="showUpload" width="500">
      <template>
        <Upload url="/product/import" :ignoreStock='ignoreStock' @close="showUpload = false;" @success="showUpload = false; load()"/>
      </template>
    </v-dialog>

    <v-row class="mb-1">
      <v-col>
        <v-btn block type="button" color="primary" v-on:click="exportExcel()">
          EXPORT
        </v-btn>
      </v-col>

      <v-col>
        <v-btn block type="button" color="primary" v-on:click="showUpload = true">
          IMPORT
        </v-btn>
      </v-col>

      <v-col>
        <v-checkbox style="margin: 0px;" v-model="ignoreStock" label="Ignore Stock"></v-checkbox>
      </v-col>

      <v-col>
        <router-link to="manage">
          <v-btn block type="button" color="primary">
            ADD NEW PRODUCT
          </v-btn>
        </router-link>
      </v-col>
    </v-row>

    <div class="filters">
      <SearchBar ref="searchBar" label='Search for product' @search="filterTable($event)" :defaultValue="search"/>

      <div class="filter">
        <v-select 
          :items="categories" 
          item-text="name" 
          item-value="id"
          v-model="filteredCategory" 
          return-object 
          label="Category" 
          @change="load()" 
          hide-details
          density="compact"/>
      </div>
    </div>

    <v-btn type="button" color="primary" class="mt-2" @click="resetStock()">RESET STOCK FOR FILTERED</v-btn>

    <div v-if="$vuetify.breakpoint.smAndDown" class="product-mobile-list">
      <div v-if="isLoading" class="product-mobile-list__loading">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <v-alert v-else-if="products.length === 0" text type="info">
        No products found.
      </v-alert>

      <v-card v-for="product in products" v-else :key="product.id" class="product-card" outlined>
        <div class="product-card__main">
          <img v-if="product.imageUrlThumbnail != null" :src="imageUrl + product.imageUrlThumbnail" alt="" />
          <div v-else class="product-card__image-placeholder">
            <v-icon>mdi-package-variant</v-icon>
          </div>

          <div class="product-card__title">
            <h3>{{ product.name }}</h3>
            <span>{{ product.category && product.category.name ? product.category.name : 'Uncategorised' }}</span>
          </div>
        </div>

        <div class="product-card__metrics">
          <div>
            <span>Stock</span>
            <strong>{{ product.currentStock }}</strong>
          </div>
          <div>
            <span>Selling</span>
            <strong>{{ formatCurrency(product.sellingPrice) }}</strong>
          </div>
          <div>
            <span>Margin</span>
            <strong>{{ product.margin }}</strong>
          </div>
        </div>

        <div class="product-card__meta">
          <div>
            <span>Supplier</span>
            <strong>{{ product.supplier && product.supplier.name ? product.supplier.name : '-' }}</strong>
          </div>
          <div>
            <span>Barcode</span>
            <strong>{{ product.barcode || '-' }}</strong>
          </div>
          <div v-if="product.tags">
            <span>Tags</span>
            <strong>{{ product.tags }}</strong>
          </div>
        </div>

        <div class="product-card__actions">
          <v-btn v-if="product.barcode == ''" type="button" text color="primary" @click="generateBarcode(product)">
            <v-icon left small>mdi-barcode-scan</v-icon>
            Barcode
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn icon color="secondary" :to="`manage/${product.id}`" aria-label="Edit product">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" aria-label="Delete product" @click="remove(product)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </v-card>
    </div>

    <v-data-table
      v-if="$vuetify.breakpoint.mdAndUp"
      :headers="headers" :items="products"
      :loading="isLoading" :items-per-page="15"
      item-key="id" class="elevation-1 mt-5"
      >

      <template v-slot:[`item.image`]="{ item }">
        <img v-if="item.imageUrlThumbnail != null" :src="imageUrl + item.imageUrlThumbnail" />
      </template>

     <template v-slot:[`item.currentStock`]="{ item }">
        <v-edit-dialog
          :return-value.sync="item.currentStock"
          @save="updateField(item, 'CurrentStock', item.currentStock)"
          large>

          <div class="editable-field">{{ item.currentStock }}</div>
          <template v-slot:input>
            <v-text-field
              v-model="item.currentStock"
              label="Edit"
              :rules="[rules.number]"
              single-line
              autofocus
              @focus="$event.target.select()"
            ></v-text-field>
          </template>

        </v-edit-dialog>
      </template>

      <template v-slot:[`item.barcode`]="{ item }">
        <v-edit-dialog
          :return-value.sync="item.barcode"
          @save="updateField(item, 'Barcode', item.barcode)"
          large>

          <div class="editable-field">{{ item.barcode }}</div>
          <template v-slot:input>
            <v-text-field
              v-model="item.barcode"
              label="Edit"
              single-line
              autofocus
              @focus="$event.target.select()"
            ></v-text-field>
          </template>

        </v-edit-dialog>
      </template>

      <template v-slot:[`item.tags`]="{ item }">
        <v-edit-dialog
          :return-value.sync="item.tags"
          @save="updateField(item, 'Tags', item.tags)"
          large>

          <div class="editable-field">{{ item.tags }}</div>
          <template v-slot:input>
            <v-text-field
              v-model="item.tags"
              label="Edit"
              single-line
              autofocus
              @focus="$event.target.select()"
            ></v-text-field>
          </template>

        </v-edit-dialog>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="product-actions">
          <v-tooltip bottom v-if="item.barcode == ''">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                aria-label="Generate barcode"
                v-bind="attrs"
                v-on="on"
                @click="generateBarcode(item)"
              >
                <v-icon small>mdi-barcode-scan</v-icon>
              </v-btn>
            </template>
            <span>Generate barcode</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="secondary" :to="`manage/${item.id}`" aria-label="Edit product" v-bind="attrs" v-on="on">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Edit product</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="error" aria-label="Delete product" v-bind="attrs" v-on="on" @click="remove(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete product</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  import SearchBar from '@/components/SearchBar.vue';
  import Upload from '@/components/Upload.vue';

  import { productService, categoryService } from '@/services';
  import { saveAs } from 'file-saver';
  import { b64toBlob } from '@/helpers'
  
  export default {
    name: 'Products',
    components: {
      SearchBar,
      Upload
    },
    data: () => ({
      imageUrl: process.env.VUE_APP_APIURL.substring(0, process.env.VUE_APP_APIURL.length - 3),
      isLoading: false,
      products: [],
      search: "",
      categories: [],
      filteredCategory: null,

      headers: [
        { text: '', align: 'start', sortable: false, filterable: false, value: 'image', width: 80 },
        { text: 'Name', align: 'start', sortable: true, filterable: true, value: 'name' },
        { text: 'Buying Price', align: 'start', sortable: true, filterable: true, value: 'buyingPriceExVAT' },
        { text: 'Selling Price', align: 'start', sortable: true, filterable: true, value: 'sellingPrice' },
        { text: 'Margin', align: 'start', sortable: true, filterable: true, value: 'margin' },
        { text: 'Tax %', align: 'start', sortable: true, filterable: true, value: 'taxPercentage' },
        { text: 'Category', align: 'start', sortable: true, filterable: true, value: 'category.name' },
        { text: 'Supplier', align: 'start', sortable: true, filterable: true, value: 'supplier.name' },
        { text: 'Current Stock', align: 'start', sortable: true, filterable: true, value: 'currentStock' },
        // { text: 'Min Stock', align: 'start', sortable: true, filterable: true, value: 'minStock' },
        // { text: 'Max Stock', align: 'start', sortable: true, filterable: true, value: 'maxStock' },
        { text: 'Barcode', align: 'start', sortable: true, filterable: true, value: 'barcode' },
        { text: 'Tags', align: 'start', sortable: true, filterable: true, value: 'tags' },
        { text: 'Actions' , align: 'end', value: 'actions' }
      ],

      showUpload: false,
      ignoreStock: true,
      rules: {
        number: value => {
          if (!isNaN(parseFloat(value)) && isFinite(value)) return true
          return 'Current stock must be a valid number'
        },
      },
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000
      }
    }),
    created () {
      this.load();
    },
    methods: {
      resetStock() {
        this.$confirm(
        {
          message: `Are you sure you want to reset the stock for ${this.products.length} products?`,
          button: {
            no: 'No',
            yes: 'Yes'
          },
          callback: async confirm => {
            if (confirm) {
              var productIds = this.products.map((p) => p.id);
              var result = await productService.resetStock(productIds);
              if (result.status == 200) {
                this.load();
              }
            }
          }
        })
      },

      showSnackbar(text, color = 'success') {
          this.snackbar.text = text;
          this.snackbar.color = color;
          this.snackbar.show = true;
      },

      // saveCurrentStock: async function(item) {
      //   try {
      //     this.isLoading = true;
      //     const response = await productService.updateCurrentStock(item.id, item.currentStock);
      //     if (response.status === 200) {
      //       this.showSnackbar('Current stock updated successfully', 'success');
      //     } else {
      //       throw new Error('Failed to update current stock');
      //     }
      //   } catch (error) {
      //     console.error('Error updating current stock:', error);
      //     this.showSnackbar('Failed to update current stock', 'error');
      //     await this.load();
      //   } finally {
      //     this.isLoading = false;
      //   }
      // },

      updateField: async function(item, fieldName, newValue) {
        try {
          console.log(item);
          this.isLoading = true;
          let response = await productService.updateProductField(item.id, fieldName, newValue);
          if (response.ok) {
            item[fieldName] = newValue;
            this.showSnackbar(`${fieldName} updated successfully`, 'success');
          } else {
            throw new Error(`Failed to update ${fieldName}`);
          }
        } catch (error) {
          console.error(`Error updating ${fieldName}:`, error);
          this.showSnackbar(`Failed to update ${fieldName}`, 'error');
          await this.load();
        } finally {
          this.isLoading = false;
        }
      },


      filterTable: function(search) {
        this.search = search;
        localStorage.setItem("@projects-filters", search);
        this.load();
      },
      load: async function() {
        this.isLoading = true;
        this.categories = await categoryService.get();
        this.products = await productService.get();

        var search = localStorage.getItem("@projects-filters", search);

        if (this.filteredCategory != null) {
          this.products = this.products.filter(p => p.categoryId == this.filteredCategory.id);
        }

        if (search && search.trim() !== "") {
          this.search = search;
          this.products = this.products.filter(product => {
            // Convert both the search term and product properties to lowercase for case-insensitive search
            const searchLower = search.toLowerCase();
            return (
              product.name?.toLowerCase().includes(searchLower) ||
              product.category?.name.toLowerCase().includes(searchLower) ||
              product.supplier?.name.toLowerCase().includes(searchLower) ||
              product.barcode?.toLowerCase().includes(searchLower)
            );
          });
        }

        this.isLoading = false;
      },
      remove: async function(product) {
        this.$confirm(
        {
          message: `Are you sure you want to delete this product?`,
          button: {
            no: 'No',
            yes: 'Yes'
          },
          callback: async confirm => {
            if (confirm) {
              this.isLoading = true;
              var response = await productService.remove(product.id);
              this.isLoading = false;
              if (response.status != 200) {
                alert("Error deleting");
                return;
              }

              this.load();
            }
          }
        })
      },
      generateBarcode: function(product) {
        this.$confirm(
        {
          message: `Are you sure you want to generate a barcode?`,
          button: {
            no: 'No',
            yes: 'Yes'
          },
          callback: async confirm => {
            if (confirm) {
              this.isLoading = true;
              var response = await productService.generateBarcode(product);
              this.isLoading = false;
              if (response.status != 200) {
                alert("Error generating barcode");
                return;
              }

              this.load();
            }
          }
        })
      },
      exportExcel: async function() {
        this.isLoading = true;
        var response = await productService.exportExcel();
        if (response.status != 200) {
          alert("Error exporting");
          return;
        }

        var base64 = await response.json();
        const blob = b64toBlob(base64, 'application/vnd.ms-excel');
        saveAs(blob, 'Products.xls');

        this.isLoading = false;
      },
      formatCurrency(value) {
        if (value == null || value === '') {
          return 'GBP 0';
        }

        const number = Number(value);
        if (Number.isNaN(number)) {
          return value;
        }

        return `GBP ${number.toFixed(2).replace(/\.00$/, '')}`;
      },
    }
  }
</script>

<style scoped>
  .product-mobile-list {
    display: grid;
    gap: 12px;
    margin-top: 14px;
  }

  .product-mobile-list__loading {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 180px;
  }

  .product-card {
    display: grid;
    gap: 12px;
    padding: 14px;
  }

  .product-card__main {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .product-card__main img,
  .product-card__image-placeholder {
    aspect-ratio: 1;
    border-radius: 8px;
    width: 52px;
  }

  .product-card__main img {
    object-fit: cover;
  }

  .product-card__image-placeholder {
    align-items: center;
    background: #effaf8;
    border: 1px solid #d5ebe7;
    display: flex;
    justify-content: center;
  }

  .product-card__image-placeholder .v-icon {
    color: #0f766e;
  }

  .product-card__title {
    min-width: 0;
  }

  .product-card__title h3 {
    color: #111827;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.25;
    margin: 0;
    overflow-wrap: anywhere;
  }

  .product-card__title span {
    color: #64748b;
    display: block;
    font-size: 12px;
    margin-top: 3px;
  }

  .product-card__metrics,
  .product-card__meta {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .product-card__meta {
    grid-template-columns: 1fr;
  }

  .product-card__metrics > div,
  .product-card__meta > div {
    background: #f8fafc;
    border: 1px solid #dfe7ef;
    border-radius: 8px;
    min-width: 0;
    padding: 9px;
  }

  .product-card__metrics span,
  .product-card__meta span {
    color: #64748b;
    display: block;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .product-card__metrics strong,
  .product-card__meta strong {
    color: #111827;
    display: block;
    font-size: 13px;
    line-height: 1.25;
    margin-top: 3px;
    overflow-wrap: anywhere;
  }

  .product-card__actions {
    align-items: center;
    border-top: 1px solid #dfe7ef;
    display: flex;
    gap: 4px;
    padding-top: 4px;
  }

  .product-card__actions .v-btn {
    box-shadow: none !important;
  }

  .product-actions {
    align-items: center;
    display: inline-flex;
    gap: 4px;
    justify-content: flex-end;
    min-width: 96px;
  }

  .product-actions .v-btn {
    box-shadow: none !important;
  }

  .editable-field {
    border-bottom: 2px dotted #2196F3; 
    display: inline-block; 
    padding-bottom: 2px; 
    cursor: pointer;
    transition: border-color 0.3s ease; 
  }

  .editable-field:hover {
    border-bottom-color: #1565C0; 
  }
</style>
